import { Preview } from '@/modules/common/models/preview/preview.ts';
import { DOC_COMMENTS_GET, DOC_FILES_GET, DOC_GANTT_ITEMS, DOC_LINKS_GET } from '@/modules/core/configs/end-points.ts';
import { usePreviewFilesStore } from '@/modules/document/stores/preview-files.ts';
import { useDocumentRowStore } from '@/modules/document/stores/document-row.store.ts';
import { useDocGanttStore } from '@/modules/document/stores/doc-gantt.store.ts';
import { useDocViewsStore } from '@/modules/document/stores/doc-views.store.ts';
import { useDocCommentsStore } from '@/modules/document/stores/doc-comments.store.ts';
import { useDocLinksStore } from '@/modules/document/stores/doc-links.store.ts';
import { useDocClientsStore } from '@/modules/document/stores/doc-clients.store.ts';
import { useDocSignaturesStore } from '@/modules/document/stores/doc-signatures.store.ts';
import { useDocAccessStore } from '@/modules/document/stores/doc-access.store.ts';
import { useDocAuditStore } from '@/modules/document/stores/doc-audit.store.ts';
import { DocumentEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import { useAccessStore } from '@/modules/core/stores/access.store.ts';
import { Access } from '@/modules/common/enums/access.enum.ts';

export class DocumentPreview extends Preview {
  static intefaceIdent = 'DOCUMENT_PREVIEW';

  fetchDataPreview(): Promise<void> {
    return new Promise((resolve, reject) => {
      this._abort().finally(() => {
        this._fetch()
          .then(() => resolve())
          .catch((e) => reject(e));
      });
    });
  }

  fetchPartlyPreview(endPoint: string) {
    switch (endPoint) {
      case DocumentEndPoint.GetList:
        return useDocumentRowStore().fetch(this._entityId!);
      case DOC_GANTT_ITEMS:
        return useDocGanttStore().fetch({ doc_id: this._entityId });
      case DOC_COMMENTS_GET:
        return useDocCommentsStore().fetch({
          doc_id: this._entityId,
          limit: this._limitLoad,
        });
      case DOC_FILES_GET: {
        const previewFilesStore = usePreviewFilesStore();
        return previewFilesStore.fetch(this._entityId!);
      }
      case DocumentEndPoint.GetClients:
        return useAccessStore().has(Access.CounterpartiesRegionsSegmentsView)
          ? useDocClientsStore().fetch({
              doc_id: this._entityId,
            })
          : Promise.resolve();
      case DOC_LINKS_GET:
        return useDocLinksStore().fetch({
          doc_id: this._entityId,
          limit: this._limitLoad,
        });
      case DocumentEndPoint.GetSignatures:
        return useDocSignaturesStore().fetch({
          doc_id: this._entityId,
        });
      case DocumentEndPoint.GetAccess:
        return useDocAccessStore().fetch({
          doc_id: this._entityId,
        });
      case DocumentEndPoint.GetAudit:
        return useDocAuditStore().fetch({
          doc_id: this._entityId,
          limit: this._limitLoad,
          offset: 0,
        });
    }
  }

  private _fetch() {
    const documentRowStore = useDocumentRowStore();
    const previewFilesStore = usePreviewFilesStore();
    const accessStore = useAccessStore();

    const promices = [
      documentRowStore.fetch(this._entityId!),
      useDocViewsStore().fetch({ doc_id: this._entityId, limit: this._limitLoad }),
      useDocCommentsStore().fetch({
        doc_id: this._entityId,
        limit: this._limitLoad,
      }),
      useDocLinksStore().fetch({ doc_id: this._entityId, limit: this._limitLoad }),
      previewFilesStore.fetch(this._entityId!),
      useDocGanttStore().fetch({ doc_id: this._entityId }),
      accessStore.has(Access.CounterpartiesRegionsSegmentsView)
        ? useDocClientsStore().fetch({
            doc_id: this._entityId,
            limit: this._limitLoad,
          })
        : [],
    ];

    return Promise.all(promices);
  }

  private _abort() {
    const promices = [
      useDocumentRowStore().abort(),
      useDocViewsStore().abort(),
      useDocCommentsStore().abort(),
      useDocClientsStore().abort(),
      useDocLinksStore().abort(),
      useDocGanttStore().abort(),
      useDocSignaturesStore().abort(),
      useDocAccessStore().abort(),
      useDocAuditStore().abort(),
    ];

    return Promise.all(promices);
  }
}
